import Component from 'ShopUi/models/component';
import { createRoot } from 'react-dom/client';
import React from 'react';
import axios from 'axios';
import AddToCartDialog from '../add-to-cart-dialog/add-to-cart-dialog';
import EventBus from 'PyzShopUi/scripts/utils/eventBus';
import { EDialogEvents } from 'PyzShopUi/scripts/modules/events/dialogEvent';
import { defMelevenArticleImageUrl } from 'PyzShopUi/scripts/modules/functions/melevenArticleImageUrl';
import i18next from 'i18next';
import EtrackerTracking from 'PyzShopUi/scripts/etracker/EtrackerTracking';
import Utils from 'PyzShopUi/scripts/utils/utils';
import {IEtrackerProduct} from 'PyzShopUi/scripts/etracker/IEtrackerTracking';
import {SEARCH_HUB_EVENT_NAME_SEARCH_COLLECTOR, SEARCH_COLLECTOR_EVENT_TYPE_BASKET } from 'PyzShopUi/scripts/constants/search-hub';

export default class AddToCartForm extends Component {

    private addToCartButton: HTMLButtonElement;
    private headerCartIcon: HTMLElement;
    private articleQuantityInput: HTMLInputElement;
    private lastAddedCartItemsQuantity: number;
    private addToCartDialogRoot;

    static readonly DATA_ADD_TO_CART_URL = 'addToCartUrl';
    static readonly DATA_CSRF_TOKEN = 'csrfToken';
    static readonly DATA_CART_PAGE_URL = 'cartPageUrl';
    static readonly DATA_RECOMMENDATION_API_URL = 'recommendationApiUrl';
    static readonly DATA_PRODUCT_NAME = 'productName';
    static readonly DATA_PRODUCT_OPTION = 'productOption';
    static readonly DATA_PRODUCT_SKU = 'productSku';
    static readonly DATA_PRODUCT_PRICE = 'productPrice';
    static readonly DATA_PRODUCT_CURRENCY = 'productCurrency';

    private readonly HTTP_STATUS_CODE_OK = 200;
    private readonly DEFAULT_MELEVEN_IMAGE_RESOLUTION = 'o_resize,w_1800,h_1800,m_limit,c_fff::o_extension,e_webp';
    private readonly TARGET_MELEVEN_IMAGE_RESOLUTION = 'o_resize,w_100,h_100,m_limit,c_fff::o_extension,e_webp';
    private _pdpInfoBox: Element;
    readonly errorMessage = 'Sorry, this item is sold out';
    readonly fallbackErrorMessage = 'An error occured.';

    protected init(): void {
        this.addToCartButton = this.querySelector('button[type="submit"]');
        this.articleQuantityInput = this.querySelector('.article__order-qty');
        this.headerCartIcon = document.querySelector('#header-cart .icon');
        this.addFieldEventListener();
        this._pdpInfoBox = document.querySelector('.notification ');

        // this is needed to unmount the component when the dialog is closed
        // otherwise the React.useEffect hook wouldn't work as you'd expect
        EventBus.getInstance().subscribe(EDialogEvents.DIALOG_CLOSED, () => {
            if (this.addToCartDialogRoot) {
                this.addToCartDialogRoot.unmount();
            }
        });
    }

    protected readyCallback(): void {
    }

    private readonly showError = (message?: string) => {
        this._pdpInfoBox.classList.remove('hide');
        const infoBoxParagraph = this._pdpInfoBox.querySelector('.i-box').querySelector('p');
        const isSapActive = this.addToCartButton.getAttribute('data-sap');
        let errorText: string;
        errorText = isSapActive === 'active'
            ? i18next.t(message ? message : this.fallbackErrorMessage, { ns: message ? 'availability' : 'layout' })
            : message ?? i18next.t(this.fallbackErrorMessage, { ns: 'layout' });

        infoBoxParagraph.innerHTML = errorText;
        this._pdpInfoBox.scrollIntoView({ block: 'end', behavior: 'smooth' });
        this.addToCartButton.disabled = false;
    }

    private readonly addFieldEventListener = (): void => {
        this.addToCartButton.addEventListener('click', (event: Event): void => {
            event.preventDefault();
            this.addToCartButton.disabled = true;
            this.lastAddedCartItemsQuantity = this.articleQuantityInput.value ? parseInt(this.articleQuantityInput.value) : 0;
            this._pdpInfoBox.classList.add('hide');
            const ajaxUrl: string | void = this.dataset[AddToCartForm.DATA_ADD_TO_CART_URL];
            const token: string = this.dataset[AddToCartForm.DATA_CSRF_TOKEN];
            const productOption: string | number = this.dataset[AddToCartForm.DATA_PRODUCT_OPTION];

            const bodyFormData = new FormData();
            const quantity = this.lastAddedCartItemsQuantity.toString();
            bodyFormData.append('quantity', quantity);
            bodyFormData.append('_token', token);
            // tslint:disable-next-line:strict-boolean-expressions
            if (productOption) {
                bodyFormData.append('product-option[]', productOption);
            }

            const etrackerProduct: IEtrackerProduct = {
                id: this.dataset[AddToCartForm.DATA_PRODUCT_SKU],
                name: this.dataset[AddToCartForm.DATA_PRODUCT_NAME],
                price: this.dataset[AddToCartForm.DATA_PRODUCT_PRICE],
                currency: this.dataset[AddToCartForm.DATA_PRODUCT_CURRENCY],
            };

            EtrackerTracking.insertToBasket(etrackerProduct, this.lastAddedCartItemsQuantity);

            const basketEvent = new CustomEvent(SEARCH_HUB_EVENT_NAME_SEARCH_COLLECTOR, { detail: { type: SEARCH_COLLECTOR_EVENT_TYPE_BASKET, id: this.dataset[AddToCartForm.DATA_PRODUCT_SKU], price: this.dataset[AddToCartForm.DATA_PRODUCT_PRICE] }, });
            dispatchEvent(basketEvent);

            axios.post(ajaxUrl, bodyFormData)
                .then(this.handleAddToCartResponse)
                .catch(_ => {
                    this.showError();
                });
        });
    }

    private readonly handleAddToCartResponse = response => {
        if (parseInt(response.data.code) !== this.HTTP_STATUS_CODE_OK) {
            this.showError(response.data.messages);
            this.addToCartButton.disabled = false;

            return;
        }

        this.updateHeaderCartCounter(this.lastAddedCartItemsQuantity);

        const addToCartDialog = document.querySelector('#add-to-cart-dialog');
        const cartPageUrl = this.dataset[AddToCartForm.DATA_CART_PAGE_URL];
        const recommendationApiUrl = this.dataset[AddToCartForm.DATA_RECOMMENDATION_API_URL];
        let productName = this.dataset[AddToCartForm.DATA_PRODUCT_NAME];

        if (!productName) {
            const productConfigurator = document.querySelector('.product-configurator');
            const brandNameElement = productConfigurator.querySelector('.brand-name');
            const brandName = brandNameElement ? brandNameElement.textContent : '' ;
            const articleTitleElement = productConfigurator.querySelector('.article-title');
            const articleTitle = articleTitleElement ? articleTitleElement.textContent : '';
            const variantNameElement = productConfigurator.querySelector('.variant-name');
            const variantName = variantNameElement ? variantNameElement.textContent : '';
            productName = `${brandName} ` + `${articleTitle} ${variantName}`;
        }

        const articleImage: HTMLImageElement = document.querySelector('.swiper-main img');
        const imageUrl = articleImage ? articleImage.src : '';
        const newImageUrl = defMelevenArticleImageUrl(imageUrl, this.DEFAULT_MELEVEN_IMAGE_RESOLUTION, this.TARGET_MELEVEN_IMAGE_RESOLUTION);

        this.addToCartDialogRoot = createRoot(addToCartDialog.querySelector('.add-to-cart-dialog-content'));

        this.addToCartDialogRoot.render(
            <AddToCartDialog
                cartPageUrl={cartPageUrl}
                fullArticleName={productName}
                articleAmount={this.lastAddedCartItemsQuantity}
                articleImageUrl={newImageUrl}
                recommendationApiUrl={recommendationApiUrl}
            />
        );

        const addToCartDialogContent = document.querySelector('.add-to-cart-dialog-content');
        const dialogFootnoteWrapper = addToCartDialogContent.querySelector('.footnote');
        const dialogFootnoteText = addToCartDialogContent.querySelector('.footnote .price-note');

        if (dialogFootnoteText) {
            dialogFootnoteText.remove();
        }

        const footnoteText = document.querySelector('.footer-footnote-wrapper .price-note').cloneNode(true);
        if (dialogFootnoteWrapper) {
            dialogFootnoteWrapper.appendChild(footnoteText);
        }

        EventBus.getInstance().publish(EDialogEvents.OPEN_DIALOG, { id: addToCartDialog.id });
    }

    private readonly updateHeaderCartCounter = (addedCartItemsCount: number) => {
        const cartCounter = this.headerCartIcon.getAttribute('data-counter');
        const articlesInCartAmount: number = cartCounter ? parseInt(cartCounter, 10) : 0;
        if (!articlesInCartAmount) {
            this.headerCartIcon.classList.add('highlight');
            this.headerCartIcon.classList.add('counter');
        }
        this.headerCartIcon.setAttribute('data-counter', (articlesInCartAmount + addedCartItemsCount).toString());
    }
}
